import React from "react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

interface MemberPerformanceData {
  teacherId: number;
  teacherName: string;
  totalMeetings: number;
  attendedMeetings: number;
  attendanceRate: number;
  performance: 'excellent' | 'good' | 'fair' | 'poor';
}

interface MemberPerformanceChartProps {
  data: MemberPerformanceData[];
}

export function MemberPerformanceChart({ data }: MemberPerformanceChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <div className="text-4xl mb-2">👥</div>
          <p>No member performance data available</p>
        </div>
      </div>
    );
  }

  const getPerformanceColor = (performance: string) => {
    switch (performance) {
      case 'excellent': return 'bg-green-500';
      case 'good': return 'bg-blue-500';
      case 'fair': return 'bg-yellow-500';
      case 'poor': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getPerformanceBadgeVariant = (performance: string) => {
    switch (performance) {
      case 'excellent': return 'default';
      case 'good': return 'secondary';
      case 'fair': return 'outline';
      case 'poor': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-4">
      {/* Performance Distribution */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {['excellent', 'good', 'fair', 'poor'].map((perf) => {
          const count = data.filter(d => d.performance === perf).length;
          const percentage = data.length > 0 ? (count / data.length) * 100 : 0;
          
          return (
            <div key={perf} className="text-center p-3 border rounded-lg">
              <div className={`w-8 h-8 mx-auto mb-2 rounded-full ${getPerformanceColor(perf)}`} />
              <div className="text-lg font-bold">{count}</div>
              <div className="text-sm text-muted-foreground capitalize">{perf}</div>
              <div className="text-xs text-muted-foreground">{percentage.toFixed(1)}%</div>
            </div>
          );
        })}
      </div>

      {/* Individual Member Performance */}
      <div className="space-y-3">
        {data
          .sort((a, b) => b.attendanceRate - a.attendanceRate)
          .map((teacher) => (
            <div key={teacher.teacherId} className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-medium">{teacher.teacherName}</div>
                  <div className="text-sm text-muted-foreground">
                    {teacher.attendedMeetings}/{teacher.totalMeetings} meetings attended
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={getPerformanceBadgeVariant(teacher.performance)}>
                    {teacher.performance}
                  </Badge>
                  <div className="text-lg font-bold">{teacher.attendanceRate}%</div>
                </div>
              </div>
              <Progress value={teacher.attendanceRate} className="h-2" />
            </div>
          ))}
      </div>

      {/* Performance Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="text-sm font-medium mb-3">Performance Summary</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Average Attendance:</span>
            <span className="ml-2 font-medium">
              {Math.round(data.reduce((sum, teacher) => sum + teacher.attendanceRate, 0) / data.length)}%
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">High Performers:</span>
            <span className="ml-2 font-medium">
              {data.filter(t => t.performance === 'excellent' || t.performance === 'good').length}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Needs Improvement:</span>
            <span className="ml-2 font-medium">
              {data.filter(t => t.performance === 'fair' || t.performance === 'poor').length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
